// src/components/roleplay/RoleplayApp.js
// Entry point for the SDR Cold-Call Roleplay tool, routed at /tools/sdr-roleplay.
// Rep-facing tabs: Practice (setup -> call -> debrief) and My History. Admins
// additionally see Personas / Team / Objections tabs (built separately —
// imported here against their agreed contract).
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { subscribeAdmins } from '../../data/admins';
import { subscribePersonas, scoreSession } from '../../data/roleplay';
import { isAdminEmail } from '../../config/admins';
import ScenarioSetup from './ScenarioSetup';
import LiveCall from './LiveCall';
import Debrief from './Debrief';
import History from './History';
import PersonaAdmin from './PersonaAdmin';
import TeamDashboard from './TeamDashboard';
import ObjectionReview from './ObjectionReview';

const Wrap = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 32px 24px 64px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 20px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

const TabsBar = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  appearance: none;
  border: none;
  background: none;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  color: ${(props) => (props.$active ? '#0e8c6a' : '#64748b')};
  border-bottom: 2px solid ${(props) => (props.$active ? '#0e8c6a' : 'transparent')};
  margin-bottom: -1px;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: ${(props) => (props.$active ? '#0e8c6a' : '#1e293b')};
  }
`;

const EmptyState = styled.div`
  color: #64748b;
  font-size: 14px;
  padding: 40px 0;
  text-align: center;
`;

const ErrorState = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 16px 20px;
  border-radius: 12px;
  font-size: 14px;
  margin: 8px 0;
`;

const LoadingBlock = styled.div`
  color: #64748b;
  font-size: 14px;
  padding: 60px 0;
  text-align: center;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 8px;
`;

const PrimaryButton = styled.button`
  appearance: none;
  border: none;
  background: #0e8c6a;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  padding: 10px 18px;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background: #0b7057;
  }
`;

const SecondaryButton = styled.button`
  appearance: none;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #1e293b;
  font-size: 14px;
  font-weight: 700;
  padding: 10px 18px;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    border-color: #0e8c6a;
    color: #0e8c6a;
  }
`;

const REP_TABS = [
  { key: 'practice', label: 'Practice' },
  { key: 'history', label: 'My History' },
];

const ADMIN_TABS = [
  { key: 'personas', label: 'Personas' },
  { key: 'team', label: 'Team' },
  { key: 'objections', label: 'Objections' },
];

function RoleplayApp({ user }) {
  const [adminRows, setAdminRows] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [personasError, setPersonasError] = useState(null);
  const [personasLoading, setPersonasLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('practice');

  // Practice state machine: 'setup' -> 'call' -> 'scoring' -> 'debrief'
  const [practiceStep, setPracticeStep] = useState('setup');
  const [scenario, setScenario] = useState(null);
  const [scoreResult, setScoreResult] = useState(null);
  const [scoringError, setScoringError] = useState(null);
  // Kept so a scoring failure can retry the SAME call instead of losing it.
  const [lastTranscript, setLastTranscript] = useState(null);
  const [lastUsage, setLastUsage] = useState(null);

  useEffect(() => {
    const unsub = subscribeAdmins((rows) => setAdminRows(rows));
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = subscribePersonas(
      (rows) => {
        setPersonas(rows);
        setPersonasError(null);
        setPersonasLoading(false);
      },
      (err) => {
        setPersonasError(
          err?.code === 'permission-denied'
            ? "You don't have permission to view personas."
            : 'Could not load personas. Please refresh and try again.'
        );
        setPersonasLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const isAdmin = isAdminEmail(user?.email, adminRows.map((r) => r.email));

  const tabs = useMemo(() => (isAdmin ? [...REP_TABS, ...ADMIN_TABS] : REP_TABS), [isAdmin]);

  // If a non-admin somehow lands on an admin tab (e.g. stale state), bounce back.
  useEffect(() => {
    if (!isAdmin && ADMIN_TABS.some((t) => t.key === activeTab)) {
      setActiveTab('practice');
    }
  }, [isAdmin, activeTab]);

  const activePersona = useMemo(
    () => personas.find((p) => p.id === scenario?.personaId) || null,
    [personas, scenario]
  );

  function handleStart(nextScenario) {
    setScenario(nextScenario);
    setScoreResult(null);
    setScoringError(null);
    setPracticeStep('call');
  }

  function runScoring(transcript, turnUsage) {
    setLastTranscript(transcript);
    setLastUsage(turnUsage || { inputTokens: 0, outputTokens: 0 });
    setPracticeStep('scoring');
    setScoringError(null);
    scoreSession({
      personaId: scenario.personaId,
      difficulty: scenario.difficulty,
      track: scenario.track,
      transcript,
      repName: user?.displayName || user?.email || '',
      turnUsage: turnUsage || { inputTokens: 0, outputTokens: 0 },
    })
      .then((result) => {
        setScoreResult(result);
        setPracticeStep('debrief');
      })
      .catch((err) => {
        console.error('scoreSession failed:', err);
        setScoringError('Could not score this call. Your transcript is saved — try again.');
        setPracticeStep('score-error');
      });
  }

  function handleEndCall(transcript, turnUsage) {
    runScoring(transcript, turnUsage);
  }

  function handlePracticeAgain() {
    setScenario(null);
    setScoreResult(null);
    setScoringError(null);
    setPracticeStep('setup');
  }

  function renderPractice() {
    if (personasLoading) {
      return <LoadingBlock>Loading personas...</LoadingBlock>;
    }
    if (personasError) {
      return <ErrorState>{personasError}</ErrorState>;
    }

    if (practiceStep === 'setup') {
      return <ScenarioSetup personas={personas} onStart={handleStart} />;
    }

    if (practiceStep === 'call') {
      return (
        <LiveCall scenario={scenario} persona={activePersona} user={user} onEnd={handleEndCall} />
      );
    }

    if (practiceStep === 'scoring') {
      return <LoadingBlock>Scoring your call...</LoadingBlock>;
    }

    if (practiceStep === 'score-error') {
      return (
        <div>
          {scoringError && <ErrorState>{scoringError}</ErrorState>}
          <ActionRow>
            <PrimaryButton
              type="button"
              onClick={() => lastTranscript && runScoring(lastTranscript, lastUsage)}
            >
              Retry scoring
            </PrimaryButton>
            <SecondaryButton type="button" onClick={handlePracticeAgain}>
              Start over
            </SecondaryButton>
          </ActionRow>
        </div>
      );
    }

    if (practiceStep === 'debrief' && scoreResult) {
      return (
        <Debrief
          result={scoreResult}
          persona={activePersona}
          onPracticeAgain={handlePracticeAgain}
          onDone={handlePracticeAgain}
        />
      );
    }

    return <EmptyState>Something went wrong. Please refresh.</EmptyState>;
  }

  function renderTab() {
    switch (activeTab) {
      case 'practice':
        return renderPractice();
      case 'history':
        return <History user={user} />;
      case 'personas':
        return isAdmin ? <PersonaAdmin user={user} /> : null;
      case 'team':
        return isAdmin ? <TeamDashboard user={user} /> : null;
      case 'objections':
        return isAdmin ? <ObjectionReview user={user} /> : null;
      default:
        return null;
    }
  }

  return (
    <Wrap>
      <PageHeader>
        <PageTitle>Cold-Call Roleplay</PageTitle>
        <PageSubtitle>Practice cold calls against an AI prospect and get scored feedback.</PageSubtitle>
      </PageHeader>

      <TabsBar>
        {tabs.map((tab) => (
          <Tab key={tab.key} $active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)}>
            {tab.label}
          </Tab>
        ))}
      </TabsBar>

      {renderTab()}
    </Wrap>
  );
}

export default RoleplayApp;
